// The house as data: rooms, the second story, and the migration from the one-room save.
// Pure module (no DOM, no state import) so hub.ts, state.ts and tests can all use it.

export interface PlacedItem { id: string; x: number; y: number }

export interface RoomState {
  id: string;
  name: string;
  placed: PlacedItem[];
  wall: string;     // theme id, e.g. 'wall.cream'
  floor: string;    // theme id, e.g. 'floor.wood'
}

export interface HouseState { rooms: RoomState[]; current: number }

export const DEFAULT_WALL = 'wall.cream';
export const DEFAULT_FLOOR = 'floor.wood';

export interface RoomDef { id: string; name: string; price: number; story: 1 | 2; blurb: string }

/** Rooms unlock in this order. The landing is "build a second story". */
export const ROOM_DEFS: RoomDef[] = [
  { id: 'living', name: 'Living room', price: 0, story: 1, blurb: 'Home base.' },
  { id: 'bedroom', name: 'Bedroom', price: 300, story: 1, blurb: 'A place for the bed and the beanbag.' },
  { id: 'kitchen', name: 'Kitchen', price: 400, story: 1, blurb: 'Table, chairs, a window over the sink.' },
  { id: 'study', name: 'Study', price: 500, story: 1, blurb: 'Desk, bookshelf, globe, telescope.' },
  { id: 'landing', name: 'Upstairs Landing', price: 800, story: 2, blurb: 'Build a second story. Stairs appear in the living room.' },
  { id: 'attic', name: 'Attic', price: 600, story: 2, blurb: 'Under the roof. Posters and the treasure chest.' },
  { id: 'tower', name: 'Tower Room', price: 700, story: 2, blurb: 'The highest window in the house.' },
];

export const ROOM_DEF_BY_ID: Record<string, RoomDef> = Object.fromEntries(ROOM_DEFS.map(r => [r.id, r]));

export function newRoom(def: RoomDef): RoomState {
  return { id: def.id, name: def.name, placed: [], wall: DEFAULT_WALL, floor: DEFAULT_FLOOR };
}

export function freshHouse(): HouseState {
  return { rooms: [newRoom(ROOM_DEFS[0])], current: 0 };
}

/**
 * Accepts the old shape `{ placed, rooms: number }`, the new shape, or nothing, and returns
 * a valid HouseState. Idempotent on the new shape.
 */
export function migrateHouse(old: unknown): HouseState {
  if (!old || typeof old !== 'object') return freshHouse();
  const o = old as Record<string, unknown>;
  if (Array.isArray(o.rooms)) {
    const rooms = (o.rooms as unknown[]).map((r, i) => {
      const rr = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
      const def = ROOM_DEF_BY_ID[String(rr.id)] ?? ROOM_DEFS[Math.min(i, ROOM_DEFS.length - 1)];
      return {
        id: typeof rr.id === 'string' ? rr.id : def.id,
        name: typeof rr.name === 'string' ? rr.name : def.name,
        placed: Array.isArray(rr.placed) ? (rr.placed as PlacedItem[]).filter(p => p && typeof p.id === 'string') : [],
        wall: typeof rr.wall === 'string' ? rr.wall : DEFAULT_WALL,
        floor: typeof rr.floor === 'string' ? rr.floor : DEFAULT_FLOOR,
      } as RoomState;
    });
    if (!rooms.length) return freshHouse();
    const current = typeof o.current === 'number' && o.current >= 0 && o.current < rooms.length ? o.current : 0;
    return { rooms, current };
  }
  // old one-room shape
  const living = newRoom(ROOM_DEFS[0]);
  if (Array.isArray(o.placed)) living.placed = (o.placed as PlacedItem[]).filter(p => p && typeof p.id === 'string');
  return { rooms: [living], current: 0 };
}

export function hasRoom(house: HouseState, id: string): boolean {
  return house.rooms.some(r => r.id === id);
}

/** 1 or 2 stories, for the exterior on the hub. */
export function houseStories(save: { house: HouseState }): 1 | 2 {
  return hasRoom(save.house, 'landing') ? 2 : 1;
}

/** The next room she can build, if any: rooms unlock strictly in ROOM_DEFS order. */
export function nextRoomDef(house: HouseState): RoomDef | undefined {
  return ROOM_DEFS.find(d => !hasRoom(house, d.id));
}

export function canBuild(house: HouseState, id: string): boolean {
  const next = nextRoomDef(house);
  return !!next && next.id === id;
}

/** every placed decor id across every room */
export function allPlacedIds(house: HouseState): Set<string> {
  const s = new Set<string>();
  for (const r of house.rooms) for (const p of r.placed) s.add(p.id);
  return s;
}
